import { logger } from '@/core/logger';
import { asyncStorage } from '@/core/storage';
import * as Application from 'expo-application';
import { Platform } from 'react-native';

/**
 * Device ID Service
 * Provides a reusable wrapper for getting device identifiers
 * Supports multiple strategies for device identification
 */
class DeviceIdService {
    private static instance: DeviceIdService;
    private cachedDeviceId: string | null = null;
    private readonly STORAGE_KEY = 'device_id_cache';

    private constructor() { }

    /**
     * Get singleton instance
     */
    static getInstance(): DeviceIdService {
        if (!DeviceIdService.instance) {
            DeviceIdService.instance = new DeviceIdService();
        }
        return DeviceIdService.instance;
    }

    /**
     * Get device ID using the best available method
     * Returns a consistent device identifier for the app installation
     */
    async getDeviceId(): Promise<string> {
        try {
            // Return cached ID if available
            if (this.cachedDeviceId) {
                return this.cachedDeviceId;
            }

            // Try to get cached ID from storage
            const cachedId = await this.getCachedDeviceId();
            if (cachedId) {
                this.cachedDeviceId = cachedId;
                return cachedId;
            }

            // Generate new device ID
            const deviceId = await this.generateDeviceId();

            // Cache the ID
            await this.cacheDeviceId(deviceId);
            this.cachedDeviceId = deviceId;

            return deviceId;
        } catch (error) {
            logger.error('DeviceIdService', 'Error getting device ID:', error);
            // Fallback to a basic identifier
            return this.getFallbackDeviceId();
        }
    }

    /**
     * Generate a new device ID using platform-specific methods
     */
    private async generateDeviceId(): Promise<string> {
        try {
            if (Platform.OS === 'ios') {
                return await this.getIOSDeviceId();
            } else if (Platform.OS === 'android') {
                return await this.getAndroidDeviceId();
            } else {
                // Web or other platforms
                return this.getWebDeviceId();
            }
        } catch (error) {
            logger.error('DeviceIdService', 'Error generating device ID:', error);
            return this.getFallbackDeviceId();
        }
    }

    /**
     * Get iOS device ID using Application.getIosIdForVendorAsync
     */
    private async getIOSDeviceId(): Promise<string> {
        try {
            const iosId = await Application.getIosIdForVendorAsync();
            if (iosId) {
                return `ios_${iosId}`;
            }
        } catch (error) {
            logger.error('DeviceIdService', 'Error getting iOS device ID:', error);
        }
        return this.getFallbackDeviceId();
    }

    /**
     * Get Android device ID using Application.getAndroidId
     */
    private async getAndroidDeviceId(): Promise<string> {
        try {
            const androidId = Application.getAndroidId();
            if (androidId) {
                return `android_${androidId}`;
            }
        } catch (error) {
            logger.error('DeviceIdService', 'Error getting Android device ID:', error);
        }
        return this.getFallbackDeviceId();
    }

    private generateWebRandom(): string {
        try {
            if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
                const buf = new Uint32Array(4);
                crypto.getRandomValues(buf);
                return Array.from(buf, (v) => v.toString(16)).join('');
            }
        } catch {
            // ignore and fallback
        }
        return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    }

    /**
     * Get web device ID using a stable random ID persisted in storage
     */
    private async getWebDeviceId(): Promise<string> {
        try {
            const randomId = this.generateWebRandom();
            return `web_${randomId}`;
        } catch (error) {
            logger.error('DeviceIdService', 'Error getting web device ID:', error);
            return this.getFallbackDeviceId();
        }
    }

    /**
     * Get fallback device ID when other methods fail
     */
    private getFallbackDeviceId(): string {
        const timestamp = Date.now().toString(36);
        const random = Math.random().toString(36).substring(2, 15);
        return `fallback_${timestamp}_${random}`;
    }

    /**
     * Cache device ID to AsyncStorage
     */
    private async cacheDeviceId(deviceId: string): Promise<void> {
        try {
            await asyncStorage.setItem(this.STORAGE_KEY, deviceId);
        } catch (error) {
            logger.error('DeviceIdService', 'Error caching device ID:', error);
        }
    }

    /**
     * Get cached device ID from AsyncStorage
     */
    private async getCachedDeviceId(): Promise<string | null> {
        try {
            return await asyncStorage.getItem(this.STORAGE_KEY);
        } catch (error) {
            logger.error('DeviceIdService', 'Error getting cached device ID:', error);
            return null;
        }
    }

    /**
     * Clear cached device ID (useful for testing or reset)
     */
    async clearDeviceId(): Promise<void> {
        try {
            await asyncStorage.removeItem(this.STORAGE_KEY);
            this.cachedDeviceId = null;
        } catch (error) {
            logger.error('DeviceIdService', 'Error clearing device ID:', error);
        }
    }

    /**
     * Get device information for debugging
     */
    async getDeviceInfo(): Promise<{
        deviceId: string;
        platform: string;
        applicationId?: string;
        installationId?: string;
    }> {
        const deviceId = await this.getDeviceId();

        try {
            const appId = Application.applicationId;
            const installationTime = await Application.getInstallationTimeAsync();

            return {
                deviceId,
                platform: Platform.OS,
                ...(appId && { applicationId: appId }),
                ...(installationTime && { installationId: installationTime.getTime().toString() })
            };
        } catch (error) {
            logger.error('DeviceIdService', 'Error getting device info:', error);
            return {
                deviceId,
                platform: Platform.OS
            };
        }
    }
}

// Export singleton instance
export const deviceIdService = DeviceIdService.getInstance();
export default deviceIdService;
